/**
 * 模型状态管理 Store
 * 管理可用模型列表和用户模型偏好设置
 */

import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import { message } from 'ant-design-vue';
import * as modelsApi from '@/api/models';
import type { ModelConfig, ModelPreferences, VideoModelParams } from '@/api/models';

// 重新导出类型，方便其他模块使用
export type { ModelConfig, ModelPreferences, VideoModelParams };

/** 系统默认偏好值（与后端 config/models.ts 保持一致） */
const DEFAULT_PREFERENCES: ModelPreferences = {
  defaultImageModel: 'seedream-3.0',
  defaultVideoModel: 'seedance-1.0-lite',
  defaultVideoDuration: 5,
  defaultVideoResolution: '720p',
};

/**
 * 模型状态管理 Store
 */
export const useModelStore = defineStore('models', () => {
  // ============================================
  // 状态定义
  // ============================================

  /** 可用图片模型列表 */
  const imageModels = ref<ModelConfig[]>([]);

  /** 可用视频模型列表 */
  const videoModels = ref<ModelConfig[]>([]);

  /** 用户模型偏好 */
  const preferences = ref<ModelPreferences>({ ...DEFAULT_PREFERENCES });

  /** 加载状态 */
  const loading = ref(false);

  // ============================================
  // 计算属性
  // ============================================

  /** 当前默认图片模型配置 */
  const currentImageModel = computed(() =>
    imageModels.value.find(m => m.id === preferences.value.defaultImageModel),
  );

  /** 当前默认视频模型配置 */
  const currentVideoModel = computed(() =>
    videoModels.value.find(m => m.id === preferences.value.defaultVideoModel),
  );

  // ============================================
  // 方法定义
  // ============================================

  /**
   * 获取可用模型列表
   */
  async function fetchModels() {
    if (loading.value) return;
    loading.value = true;
    try {
      const res = await modelsApi.getModels();
      imageModels.value = res.data?.imageModels ?? [];
      videoModels.value = res.data?.videoModels ?? [];
    } catch (error) {
      console.error('[模型Store] 获取模型列表失败', error);
    } finally {
      loading.value = false;
    }
  }

  /**
   * 获取用户模型偏好
   */
  async function fetchPreferences() {
    try {
      const res = await modelsApi.getModelPreferences();
      if (res.data) {
        preferences.value = { ...DEFAULT_PREFERENCES, ...res.data };
      }
    } catch (error) {
      console.error('[模型Store] 获取偏好失败', error);
    }
  }

  /**
   * 更新用户模型偏好
   * @param prefs 要更新的偏好字段
   */
  async function updatePreferences(prefs: Partial<ModelPreferences>) {
    try {
      const res = await modelsApi.updateModelPreferences(prefs);
      if (res.data) {
        preferences.value = { ...DEFAULT_PREFERENCES, ...res.data };
      }
      message.success('模型偏好已保存');
      return true;
    } catch (error) {
      console.error('[模型Store] 更新偏好失败', error);
      return false;
    }
  }

  /**
   * 根据 ID 查找模型配置
   * @param id 模型ID
   */
  function getModelById(id: string): ModelConfig | undefined {
    return imageModels.value.find(m => m.id === id)
      ?? videoModels.value.find(m => m.id === id);
  }

  return {
    // 状态
    imageModels,
    videoModels,
    preferences,
    loading,
    // 计算属性
    currentImageModel,
    currentVideoModel,
    // 方法
    fetchModels,
    fetchPreferences,
    updatePreferences,
    getModelById,
  };
});
